// apps/web/src/components/providers/navigation-history-provider.tsx

'use client';

import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocale } from '@/i18n/useLocale';
import {
  useNavigationHistory,
  UseNavigationHistoryReturn,
} from '@/hooks/use-navigation-history';
import { resolveDefaultPath, addLocale } from '@/lib/navigation-config';

export interface NavigationHistoryContextValue extends UseNavigationHistoryReturn {
  goBack: (defaultPath?: string) => void;
  getBackPath: (defaultPath?: string) => string;
}

export const NavigationHistoryContext = createContext<NavigationHistoryContextValue | null>(null);

export function NavigationHistoryProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const locale = useLocale();
  const navigationHistory = useNavigationHistory();

  // Auto-track navigation when pathname changes
  useEffect(() => {
    if (pathname) {
      navigationHistory.pushPath(pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  /**
   * Get the back target path (locale-aware).
   * Priority: history > provided default > config default > /dashboard
   */
  const getBackPath = (defaultPath?: string): string => {
    // Check history first (second-to-last item is the previous page)
    const previousPath = navigationHistory.history[navigationHistory.history.length - 2]?.path;
    if (previousPath) {
      return addLocale(previousPath, locale.locale);
    }

    // Use provided default
    if (defaultPath) {
      return addLocale(defaultPath, locale.locale);
    }

    // Use config-based default
    const currentPath = pathname || '';
    const configDefault = resolveDefaultPath(currentPath.replace(/^\/[a-z]{2}-[A-Z]{2}/, ''));
    if (configDefault) {
      return addLocale(configDefault, locale.locale);
    }

    // Fallback to dashboard
    return addLocale('/dashboard', locale.locale);
  };

  /**
   * Navigate back to the previous page or default.
   */
  const goBack = (defaultPath?: string) => {
    // Get the back path BEFORE modifying history
    const targetPath = getBackPath(defaultPath);

    // Pop current page from history
    navigationHistory.popPath();

    // Navigate to the target
    navigate(targetPath);
  };

  return (
    <NavigationHistoryContext.Provider
      value={{
        ...navigationHistory,
        goBack,
        getBackPath,
      }}
    >
      {children}
    </NavigationHistoryContext.Provider>
  );
}

/**
 * Hook to access navigation history context.
 * Must be used within NavigationHistoryProvider.
 */
export function useNavigationHistoryContext(): NavigationHistoryContextValue {
  const context = useContext(NavigationHistoryContext);
  if (!context) {
    throw new Error('useNavigationHistoryContext must be used within NavigationHistoryProvider');
  }
  return context;
}
